import { useEffect, useState } from "react";
import { formatBytes } from "../lib/format";

/**
 * Generic artifact preview modal. Fetches a presigned S3 URL, detects
 * the format from the filename, and renders accordingly:
 *
 *   .json → pretty-printed with 2-space indent
 *   .csv  → table (header + first N rows)
 *   .txt / .log / unknown → monospace plain text
 *
 * Falls back to "Couldn't preview — download instead" when the body
 * isn't text (e.g. canvasUpload.zip is binary; preview is blocked
 * upstream by the SisBundleDetail page only offering preview for
 * text-y types).
 */

const MAX_CSV_PREVIEW_ROWS = 200;
const MAX_TEXT_PREVIEW_BYTES = 2 * 1024 * 1024; // 2MB cap for safety

interface PreviewState {
  kind: "json" | "csv" | "text" | "binary";
  /** Raw text body. */
  body: string;
  /** For CSV: parsed table. */
  csv?: { header: string[]; rows: string[][]; totalRows: number };
  bytes: number;
  truncated: boolean;
}

export function ArtifactPreviewModal({
  title,
  filename,
  url,
  onClose,
  onDownload,
}: {
  title: string;
  /** Used to detect format (json/csv/text). */
  filename: string;
  /** Presigned S3 URL — should have disposition=inline so the browser
   *  doesn't try to download it. */
  url: string | null;
  onClose: () => void;
  onDownload?: () => void;
}) {
  const [state, setState] = useState<PreviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setState(null);
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const contentLength = Number(r.headers.get("content-length") ?? 0);
        // Stream to text but bail out if absurdly large.
        const text = await r.text();
        const bytes = contentLength || new Blob([text]).size;
        const truncated = text.length > MAX_TEXT_PREVIEW_BYTES;
        const body = truncated ? text.slice(0, MAX_TEXT_PREVIEW_BYTES) : text;
        if (cancelled) return;
        setState(buildState(filename, body, bytes, truncated));
      })
      .catch((e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url, filename]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal csv-preview-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="artifact-preview-title"
      >
        <div className="csv-preview-head">
          <div>
            <h2 className="modal-title" id="artifact-preview-title">
              {title}
            </h2>
            {state && (
              <div className="muted small csv-preview-stats">
                <code>{filename}</code>
                {" · "}
                {formatBytes(state.bytes)}
                {state.csv && (
                  <>
                    {" · "}
                    {state.csv.totalRows.toLocaleString()} row
                    {state.csv.totalRows === 1 ? "" : "s"}
                    {state.csv.totalRows > MAX_CSV_PREVIEW_ROWS && (
                      <> (showing first {MAX_CSV_PREVIEW_ROWS})</>
                    )}
                  </>
                )}
                {state.truncated && <> · preview truncated at 2MB</>}
              </div>
            )}
          </div>
          <div className="csv-preview-head-actions">
            {onDownload && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={onDownload}
                title="Download the full file"
              >
                Download
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              onClick={onClose}
              aria-label="Close preview"
            >
              Close
            </button>
          </div>
        </div>

        <div className="csv-preview-body">
          {loading && <div className="muted">Loading preview…</div>}
          {error && (
            <div className="error">
              Couldn't load preview: {error}
              {onDownload && <> — try the download button instead.</>}
            </div>
          )}
          {state && !error && renderBody(state)}
        </div>
      </div>
    </div>
  );
}

function buildState(
  filename: string,
  body: string,
  bytes: number,
  truncated: boolean,
): PreviewState {
  const lower = filename.toLowerCase();

  if (lower.endsWith(".json")) {
    // Pretty-print. If body is already pretty, this is a near no-op.
    let pretty = body;
    try {
      pretty = JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      /* leave as-is — render the raw text */
    }
    return { kind: "json", body: pretty, bytes, truncated };
  }

  if (lower.endsWith(".csv")) {
    return {
      kind: "csv",
      body,
      csv: parseCsv(body),
      bytes,
      truncated,
    };
  }

  if (lower.endsWith(".txt") || lower.endsWith(".log")) {
    return { kind: "text", body, bytes, truncated };
  }

  // Unknown extension — try a sniff: if the first few bytes look like
  // JSON or CSV header, treat accordingly; otherwise plain text.
  const trimmed = body.slice(0, 200).trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    let pretty = body;
    try {
      pretty = JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      /* keep raw */
    }
    return { kind: "json", body: pretty, bytes, truncated };
  }
  return { kind: "text", body, bytes, truncated };
}

function renderBody(state: PreviewState): React.ReactElement {
  if (state.kind === "csv" && state.csv) {
    const rowsToShow = state.csv.rows.slice(0, MAX_CSV_PREVIEW_ROWS);
    return (
      <>
        <table className="csv-preview-table">
          <thead>
            <tr>
              {state.csv.header.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsToShow.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {state.csv.totalRows > MAX_CSV_PREVIEW_ROWS && (
          <div className="muted small csv-preview-truncated">
            …and {(state.csv.totalRows - MAX_CSV_PREVIEW_ROWS).toLocaleString()}{" "}
            more row
            {state.csv.totalRows - MAX_CSV_PREVIEW_ROWS === 1 ? "" : "s"}.
            Download the file to see everything.
          </div>
        )}
      </>
    );
  }

  // JSON + plain text both render as monospace <pre>. JSON has been
  // pretty-printed upstream in buildState.
  return (
    <pre
      style={{
        fontFamily: "var(--mono)",
        fontSize: 12,
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        margin: 0,
      }}
    >
      {state.body}
    </pre>
  );
}

function parseCsv(text: string): {
  header: string[];
  rows: string[][];
  totalRows: number;
} {
  const allLines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (allLines.length === 0) return { header: [], rows: [], totalRows: 0 };
  const header = splitQuoted(allLines[0]!);
  const dataLines = allLines.slice(1);
  const previewLines = dataLines.slice(0, MAX_CSV_PREVIEW_ROWS);
  return {
    header,
    rows: previewLines.map(splitQuoted),
    totalRows: dataLines.length,
  };
}

function splitQuoted(line: string): string[] {
  const matches = line.match(/"([^"]*)"/g);
  if (matches) return matches.map((m) => m.slice(1, -1));
  return line.split(",");
}
